import { ISelectModel } from 'Types/Litium';
import { IFieldValue } from 'Scripts/Slices/loginPage';
import { IInputValidation } from 'Components/Forms/RenderFormField';
import ArrowIcon from 'Icons/caretGsd.svg?react';

interface SelectFieldProps {
    field: ISelectModel;
    handleInputChange: (data: IFieldValue) => void;
    error: IInputValidation;
    loading?: boolean;
    executeFunctionByName: (
        functionName: string,
        data: Record<string, any>,
        event: EventTarget & HTMLSelectElement
    ) => void;
}

const SelectField = ({
    field,
    handleInputChange,
    error,
    loading,
    executeFunctionByName,
}: SelectFieldProps) => {
    const {
        name,
        label,
        placeHolder,
        handlers,
        options,
        type,
        validationRequiredMessage,
        disabled,
        required,
    } = field;

    const selectedOption = options.find(option => option.selected === true);

    const hasError =
        error &&
        'isValid' in error &&
        (!error?.isValid || !error?.isRequiredCriteriaMet);

    return (
        <div className="relative mb-5 flex flex-col px-[1px]">
            <label htmlFor={name}>
                <div>
                    <span className="mb-2 block font-roboto text-base font-light leading-none text-black">
                        {label}
                        {required && ' *'}
                    </span>
                    <div className="relative">
                        <select
                            className={`mb-4 block w-full basis-full scroll-m-6 appearance-none rounded-none border border-solid border-transparent bg-white pl-4 pr-8 font-roboto text-base font-light text-black outline outline-1 outline-black transition-[outline,_border,_box-shadow,_border-color] duration-layout ease-in-out focus:border-black disabled:bg-silver ${
                                hasError
                                    ? 'border-errorRed text-errorRed outline-errorRed'
                                    : 'border-black'
                            }  ${loading ? 'pointer-events-none' : ''}`}
                            defaultValue={selectedOption?.value ?? ''}
                            name={disabled ? '' : name}
                            id={name}
                            required={disabled ? false : required}
                            disabled={disabled}
                            onChange={e => {
                                if (handlers?.onChange) {
                                    const { functionName, data } =
                                        handlers.onChange;
                                    executeFunctionByName(
                                        functionName,
                                        data,
                                        e.target as HTMLSelectElement
                                    );
                                }
                                return handleInputChange({
                                    value: e.target.value,
                                    type,
                                    name,
                                });
                            }}
                        >
                            {disabled ? (
                                <option value={selectedOption.value}>
                                    {selectedOption.text}
                                </option>
                            ) : (
                                <>
                                    <option value="" disabled>
                                        {placeHolder}
                                    </option>
                                    {options.map((option, index) => (
                                        <option
                                            value={option.value}
                                            key={index}
                                        >
                                            {option.text}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                        {!disabled && (
                            <ArrowIcon className="absolute bottom-1/2 right-0 h-4 w-4 -translate-x-1/2 translate-y-1/2 rotate-90 text-black" />
                        )}
                    </div>
                </div>
                {disabled && (
                    <input
                        type="hidden"
                        name={name}
                        value={selectedOption.value}
                    />
                )}
            </label>
            {error &&
                'isValid' in error &&
                (!error?.isValid || !error?.isRequiredCriteriaMet) && (
                    <span className="-mt-2.5 text-xs text-errorRed">
                        {validationRequiredMessage}
                    </span>
                )}
        </div>
    );
};

export default SelectField;
